import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { useMediaQuery } from 'usehooks-ts'


import {
  GQL_SCOPED_POPULAR_PRODUCTS,
  ScopedPopularProductsQueryVariables,
} from '@pages/catalog/model/scopedProductQueries'
import stylesLayout from '@pages/catalog/ui/styles.module.scss'

import { readProductsScope } from '@features/landing-page-builder'
import { useAddressStore } from '@features/shipping-methods'

import { CategoriesBranch } from '@widgets/categories-branch'
import ProductsInfiniteScroll from '@widgets/products-infinite-scroll'


import { IMAGE_SIZES, URL_PAGE_CATALOG } from '@shared/contants'
import { PopularProductsQuery, Product } from '@shared/generated'
import useFetchMorePagination from '@shared/hooks/useFetchMorePagination'
import { cn } from '@shared/lib/cn'
import Breadcrumbs from '@shared/ui/breadcrumbs'
import Container from '@shared/ui/container'
import PageGrid from '@shared/ui/page-grid'
import TitleWithCount from '@shared/ui/title-with-count'
import Wrapper from '@shared/ui/wrapper'
import getNoun from '@shared/uttils/getNoun'

import styles from './styles.module.scss'

const LIMIT = 12

export default function PopularProductsPage () {
  const { t: tn } = useTranslation('navigation')
  const { t: tp } = useTranslation('product')
  const isTablet = useMediaQuery('(max-width: 991px)')
  const { shopId } = useAddressStore()

  // Keeps the scope a landing's "Популярные товары" block linked here with.
  const { search } = useLocation()
  const { filter } = readProductsScope(search)

  const { data: popularProductsData, loading: popularProductsLoading, fetchMore } = useQuery<
    PopularProductsQuery,
    ScopedPopularProductsQueryVariables
  >(GQL_SCOPED_POPULAR_PRODUCTS, {
    variables: {
      shopId: shopId,
      style: IMAGE_SIZES.LARGE,
      filter,
      pager: {
        limit: LIMIT
      }
    }
  })

  const { hasMore, loadingMore, fetchNextPage } =
    useFetchMorePagination<
      PopularProductsQuery,
      ScopedPopularProductsQueryVariables
    >({
      loading: popularProductsLoading,
      fetchMore,
      pageVariable: 'pager',
      usePager: true,
      variables: {
        shopId,
        filter,
        pager: { limit: LIMIT }
      },
      updaterFunction (cached, fetched) {
        const prev = cached?.popularProducts?.edges ?? []
        const curr = fetched?.popularProducts?.edges ?? []

        return {
          hasMore: curr.length > 0,
          result: {
            ...cached,
            popularProducts: {
              ...cached.popularProducts,
              edges: [ ...prev, ...curr ],
            },
          } as PopularProductsQuery,
        }
      },
    })

  const totalProducts = popularProductsData?.popularProducts?.pageInfo?.total ?? 0

  return (
    <Container>
      <PageGrid>
        <Wrapper className={styles.wrapper}>
          <div className={stylesLayout.grid}>
            <Breadcrumbs
              className={stylesLayout.breadcrumbs}
              links={[
                {
                  title: tn('catalog'),
                  to: URL_PAGE_CATALOG
                },
                {
                  title: tp('popular')
                }
              ]}
            />
            {!isTablet && (
              <div className={cn(stylesLayout.aside, styles.aside)}>
                <div className={styles.aside}>
                  <CategoriesBranch className={styles.aside__content} />
                </div>
              </div>
            )}
            <div className={cn(stylesLayout.content)}>
              <div className={styles.root__top}>
                <TitleWithCount
                  className={styles.root__title}
                  title={tp('popular')}
                >
                  {totalProducts !== null && (
                    <>
                      {totalProducts}{' '}
                      {getNoun(
                        totalProducts as number,
                        tp('product.1'),
                        tp('product.2'),
                        tp('product.3'),
                      )}
                    </>
                  )}
                </TitleWithCount>
              </div>
              <ProductsInfiniteScroll
                products={popularProductsData?.popularProducts?.edges as Product[]}
                loading={popularProductsLoading}
                fetchNextPage={fetchNextPage}
                hasMore={hasMore}
                loadingMore={loadingMore}
              />
            </div>
          </div>
        </Wrapper>
      </PageGrid>
    </Container>
  )
}